/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Components
import EventCard from 'ui/components/meetings/EventCard.vue';
import MeetingDetailsPopover from 'ui/components/meetings/MeetingDetailsPopover.vue';
import ResourceCard from 'ui/components/meetings/ResourceCard.vue';
import ResourcesFilter from 'ui/components/meetings/ResourcesFilter.vue';
import SchedulerView from 'shared/ui/components/calendars/SchedulerView.vue';

const __vue_script__ = {
    name: 'Calendar',

    components: {
        MeetingDetailsPopover,
        ResourcesFilter,
        SchedulerView
    },

    props: {
        event: {
            type: Object,
            required: true
        },

        user: {
            type: Object,
            required: true
        },

        module: {
            type: Object,
            required: true
        },

        searchTerm: {
            type: String,
            default: ''
        },

        showSessions: {
            type: Boolean,
            default: false
        },

        reloadSpy: {
            type: Number,
            default: 0
        },

        actions: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        const storageLocKey = `selected_locations_in_meetings_calendar_${this.event._id}`;
        const storagePaxKey = `selected_participants_in_meetings_calendar_${this.event._id}`;
        const storageProspectsKey = `selected_prospects_in_meetings_calendar_${this.event._id}`;

        return {
            currentDateTs: this.$utils.time.startOfDayFromDateAndTz(new Date(), this.event.timezone),
            ready: false,
            events: [],
            resources: [],
            popovers: [],
            selectedLocations: this.$services.storage.get(storageLocKey) || [],
            selectedParticipants: this.$services.storage.get(storagePaxKey) || [],
            selectedProspects: this.$services.storage.get(storageProspectsKey) || [],
            dailySessions: [],
            sessions: [],
            meetings: [],
            participantsMap: new Map(),
            locations: [],
            calendarConfig: {
                clickToCreate: 'single'
            },
            EventCard,
            ResourceCard,
            storageLocKey,
            storagePaxKey,
            storageProspectsKey
        };
    },

    computed: {
        visibleEvents() {
            if (this.searchTerm?.length < 3) {
                return this.events;
            }

            return this.events.filter(event => event.__search.includes(this.searchTerm.toLowerCase()));
        },

        visibleResources() {
            const allSelectedResIds = [...this.selectedLocations, ...this.selectedParticipants, ...this.selectedProspects];
            return this.resources.filter(r =>
                allSelectedResIds.includes(r.origin?._id) ||
                allSelectedResIds.includes(r.origin?.fp_ext_id) ||
                r.is_agenda);
        },

        invalid() {
            const invalid = [];
            for (const { origin } of this.visibleResources) {
                const invaliditySlots = this.$utils.calendar.getInvaliditySlotsForDay({
                    eventTimezone: this.event.timezone,
                    currentDateTs: this.currentDateTs,
                    location: origin,
                });
                for (const d of invaliditySlots) {
                    invalid.push(d);
                }
            }

            return invalid;
        },
    },

    watch: {
        async showSessions() {
            await this.updateResources();
            this.updateEvents();
        },

        selectedLocations() {
            this.$services.storage.set(this.storageLocKey, this.selectedLocations);
        },

        selectedParticipants() {
            this.$services.storage.set(this.storagePaxKey, this.selectedParticipants);
        },

        selectedProspects() {
            this.$services.storage.set(this.storageProspectsKey, this.selectedProspects);
        },

        reloadSpy() {
            this.loadData();
        }
    },

    async created() {
        try {
            this.locations = await this.$services.appScripts.callAppScript(this.event._id, 'meetings/locations');
        } catch (error) {
            console.error('[Calendar] Could not load locations', error);
        }
        await this.loadData(true);
        this.ready = true;
    },

    methods: {
        /**
         * Loads all the locations.
         */
        async loadData(updateSelection) {
            try {
                const { sessions, meetings, participants } = await this.$services.appScripts.callAppScript(this.event._id, 'meetings/calendar', {
                    timestamp: this.currentDateTs
                });

                this.$utils.calendar.reconcileParticipants(meetings, participants);

                for (const p of participants) {
                    this.participantsMap.set(p._id, p);
                }
                this.meetings = meetings;
                this.sessions = sessions;

                await this.updateResources(updateSelection);
                this.updateEvents(updateSelection);

            } catch (error) {
                console.error('[Calendar] Could not load meetings data', error);
            }
        },

        /**
         * Sets up the location resources for the calendar.
         *
         * @param {Boolean} updateSelection - Whether to update the selected locations.
         */
        setupLocations(updateSelection = false) {
            const resources = [];

            if (this.showSessions) {
                resources.push({
                    id: 'sessions',
                    title: this.$t('meetings.calendar.filters.daily_sessions'),
                    type: 'sessions',
                    is_agenda: true,
                    origin: { _id: 'sessions' }
                });
            }

            for (const location of this.locations) {
                location.fp_type = 'meeting_location';
                const resource = this.$utils.calendar.paxOrLocationToCalendarResource(location);
                resources.push(resource);
            }

            if (updateSelection && this.selectedLocations.length === 0) {
                this.selectedLocations = resources.filter(r => r.type === 'meeting_location' && r.origin?._id).map(r => r.origin._id);
            } else {
                // Remove selected locations that are not in the list of available locations (e.g. deleted locations)
                this.selectedLocations = this.selectedLocations.filter(id => resources.find(r => r.origin?._id === id));
            }

            return resources;
        },

        /**
         * Sets up the participants for the calendar.
         */
        async setupParticipants() {
            const resources = [];
            const idsOfPaxWithNoData = this.selectedParticipants.filter(id => !this.participantsMap.has(id));
            const idsOfProspectsWithNoData = this.selectedProspects.filter(id => !this.participantsMap.has(id));

            if (idsOfPaxWithNoData.length > 0) {
                const paxDocs = await this.$services.backlinks.queryBacklinksWithKeys(this.event._id, 'person', '_id', idsOfPaxWithNoData);
                paxDocs.forEach(pax => this.participantsMap.set(pax._id, pax));
            }

            if (idsOfProspectsWithNoData.length > 0) {
                const prospects = await this.$services.documents.getDocsByType(this.event._id, 'prospect', { ids: idsOfProspectsWithNoData });
                prospects.forEach(prospect => {
                    prospect.fp_type = 'prospect';
                    this.participantsMap.set(prospect._id, prospect);
                });
            }

            [...this.selectedParticipants, ...this.selectedProspects].forEach(id => {
                const participant = this.participantsMap.get(id);
                resources.push(this.$utils.calendar.paxOrLocationToCalendarResource(participant));
            });

            return resources;
        },

        /**
         * Sets up the meetings for the calendar.
         *
         * @param {Meeting[]} meetings - An array of all meetings.
         */
        setupMeetings(meetings) {
            const events = [];
            for (const meeting of meetings) {
                meeting.room = this.resources.find(r => r.id === meeting.meeting_location);
                events.push(this.$utils.calendar.meetingToCalendarEvent(meeting));
            }

            return events;
        },

        /**
         * Sets up the sessions for the calendar.
         *
         * @param {Array} sessions - An array of session objects.
         */
        setupSessions(sessions) {
            this.dailySessions = sessions;

            if (!this.showSessions) {
                return [];
            }

            const events = [];
            for (const session of sessions) {
                session.room = 'sessions';
                events.push(this.$utils.calendar.sessionToCalendarEvent(session));
            }

            return events;
        },

        /**
         * Updates the resources based on the given selection.
         *
         * @param {boolean} updateSelection - Indicates whether to update the selection.
         */
        async updateResources(updateSelection) {
            this.resources = [...this.setupLocations(updateSelection), ...await this.setupParticipants()];
        },

        /**
         * Updates the events in the calendar.
         *
         * This method is responsible for updating the events displayed in the calendar.
         * It should be called whenever there is a change in the events data.
         */
        updateEvents() {
            this.events = [...this.setupMeetings(this.meetings), ...this.setupSessions(this.sessions)];
        },

        /**
         * Handles the event click event.
         *
         * @param {Object} args - The event click arguments.
         */
        onEventClick(args) {
            if (args.event.resource === 'sessions') {
                console.warn('[Calendar] Session click not implemented');
                return;
            }

            const popover = {
                ref: args.domEvent.target,
                details: args.event.origin
            };

            if (!popover.details) {
                return;
            }

            this.addPopover(popover);
        },

        /**
         * Adds a popover to the calendar.
         *
         * @param {Object} popover - The popover object to be added.
         */
        addPopover(popover) {
            console.info('[Calendar] Adding popover', popover);
            if (!this.popovers.find(p => p.details._id === popover.details._id)) {
                this.popovers.push(popover);
            }
        },

        /**
         * Removes the specified popover.
         *
         * @param {Object} popover - The popover to be removed.
         */
        removePopover(popover) {
            this.popovers = this.popovers.filter(p => p.details._id !== popover.details._id);
        },

        /**
         * Handler for meeting deletion.
         *
         * @param {Object} popover - The popover object associated with the meeting.
         */
        onDeleteMeeting(popover) {
            this.removePopover(popover);
            this.loadData();
        },

        /**
         * Updates the filters for the calendar.
         *
         * @param {Object} filters - The filters object.
         * @param {Array} filters.locations - The locations to filter by.
         * @param {Array} filters.participants - The participants to filter by.
         * @param {Array} filters.prospects - The prospects to filter by.
         */
        updateFilters({ locations, participants, prospects }) {
            this.selectedLocations = locations;
            this.selectedParticipants = participants;
            this.selectedProspects = prospects;
            this.updateResources();
        },

        /**
         * Callback function that is called when the selected date changes.
         *
         * @param {Object} date - The new selected date.
         */
        onSelectedDateChange({ date }) {
            console.info('[Calendar] Selected date changed:', date);
            this.currentDateTs = this.$utils.time.startOfDayFromDateAndTz(date, this.event.timezone);
            this.loadData();
        },

        /**
         * Handles the event creation.
         *
         * @param {Object} event - The event object.
         */
        onEventCreated({ event }) {
            const resource = this.resources.find(r => r.id === event.resource);
            this.$emit('create', event, resource);
        },

        /**
         * Handles the event when the editor is closed.
         *
         * @param {Object} popover - The popover object.
         * @param {string} reason - The reason for closing the editor.
         * @param {object} [model] - The model object.
         */
        onCloseEditor(popover, reason, model) {
            console.info('[Calendar] Closing popover', reason);
            this.removePopover(popover);

            if (reason?.startsWith('editor-')) {
                this.$emit('editor-closed', reason, model);
            }
        },

        /**
         * Removes an event from the calendar.
         *
         * @param {Object} event - The event to be removed.
         */
        removeEvent(event) {
            this.$refs.scheduler.removeEvent(event);
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.ready)?_c('div',{ref:"wrapper",staticClass:"calendar"},[_c('resources-filter',{attrs:{"event":_vm.event,"selected-locations":_vm.selectedLocations,"selected-participants":_vm.selectedParticipants,"selected-prospects":_vm.selectedProspects},on:{"input":_vm.updateFilters}}),_c('scheduler-view',{ref:"scheduler",attrs:{"event":_vm.event,"events":_vm.visibleEvents,"resources":_vm.visibleResources,"invalid":_vm.invalid,"drag-to-move":false,"drag-to-resize":false,"event-component":_vm.EventCard,"resource-component":_vm.ResourceCard,"calendar-config":_vm.calendarConfig},on:{"event-click":_vm.onEventClick,"selected-date-change":_vm.onSelectedDateChange,"event-created":_vm.onEventCreated}}),_vm._l((_vm.popovers),function(popover){return _c('meeting-details-popover',{key:popover.details._id,attrs:{"container":".calendar","event":_vm.event,"actions":_vm.actions,"module":_vm.module,"meeting":popover.details,"virtual-element":popover.ref,"on-updated":_vm.loadData,"on-error":_vm.loadData,"on-deleted":function () { return _vm.onDeleteMeeting(popover); },"on-close":function (reason, model) { return _vm.onCloseEditor(popover, reason, model); }}})})],2):_vm._e()}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "Calendar.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__